/**
 * FileUpload Component (Phase-2)
 *
 * Drag-and-drop / click-to-browse file picker for PDF and image uploads.
 * Mirrors the backend FileStorageService pattern: the parent page builds a
 * multipart FormData with the chosen file and POSTs it to the relevant module
 * endpoint, which stores the file on disk and returns the record (with fileUrl).
 *
 * @author Civilhealth Development Team
 */
import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

export interface FileUploadProps {
  /** Accepted file extensions/MIME types. Default: PDF + common images. */
  accept?: string;
  /** Max file size in bytes. Default: 20MB (matches spring multipart max). */
  maxSize?: number;
  /** Called when a file is chosen (validated). Parent builds FormData + POSTs. */
  onFileSelected: (file: File) => void;
  /** Optional currently-selected file name (controlled). */
  fileName?: string;
  /** Optional preview URL (e.g. an image or PDF URL returned by the backend). */
  fileUrl?: string;
  /** Optional upload-in-progress flag. */
  uploading?: boolean;
  /** Optional label. */
  label?: string;
  /** Optional clear handler. */
  onClear?: () => void;
  /** Disabled state. */
  disabled?: boolean;
}

const DEFAULT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png';
const DEFAULT_MAX_SIZE = 20 * 1024 * 1024; // 20MB

const FileUpload: React.FC<FileUploadProps> = ({
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  onFileSelected,
  fileName,
  fileUrl,
  uploading = false,
  label = 'Drag & drop a file here, or click to browse',
  onClear,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localFileName, setLocalFileName] = useState<string | undefined>(fileName);

  const validateAndEmit = (file: File) => {
    setError(null);
    if (file.size > maxSize) {
      setError(`File too large. Max size is ${(maxSize / (1024 * 1024)).toFixed(0)}MB.`);
      return;
    }
    setLocalFileName(file.name);
    onFileSelected(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndEmit(file);
    // reset so picking the same file twice still fires
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndEmit(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const isImage = (name?: string) =>
    !!name && /\.(jpg|jpeg|png)$/i.test(name);
  const isPdf = (name?: string) => !!name && /\.pdf$/i.test(name);

  const displayName = localFileName || fileName;

  return (
    <Box>
      <Box
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: `2px dashed ${dragOver ? '#2563EB' : '#CBD5E1'}`,
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          bgcolor: dragOver ? alpha('#2563EB', 0.06) : '#F8FAFC',
          transition: 'all 0.2s ease',
          opacity: disabled || uploading ? 0.7 : 1,
          '&:hover': {
            borderColor: disabled || uploading ? '#CBD5E1' : '#93C5FD',
            bgcolor: disabled || uploading ? '#F8FAFC' : alpha('#2563EB', 0.04),
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />
        {uploading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Uploading...</Typography>
          </Box>
        ) : displayName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            {isImage(displayName) ? <ImageIcon color="primary" /> : <PdfIcon color="error" />}
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1A1A2E' }} noWrap>
              {displayName}
            </Typography>
            {onClear && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocalFileName(undefined);
                  onClear();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <UploadIcon sx={{ fontSize: 36, color: '#6B7280' }} />
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="caption" color="text.disabled">
              PDF, JPG, PNG (max {(maxSize / (1024 * 1024)).toFixed(0)}MB)
            </Typography>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{error}</Alert>}

      {fileUrl && displayName && !uploading && (
        <Box sx={{ mt: 1.5 }}>
          {isImage(displayName) ? (
            <img
              src={fileUrl}
              alt={displayName}
              style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 12, border: '1px solid #E5E7EB' }}
            />
          ) : (
            <Button
              size="small"
              variant="outlined"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Preview / Download
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;